import type { ComponentType } from 'react';

import { BookOpenIcon, ClipboardCheckIcon, ClipboardIcon, ClipboardListIcon, FileTextIcon, FolderIcon, HomeIcon, StarIcon } from '../layout/icons';

// Labels are stored as keys and translated at render (module-level t() would freeze the language).
export type HomeNavLabelKey = 'nav.home' | 'nav.lectureLibrary' | 'nav.questionBank' | 'nav.documentLibrary' | 'nav.sampleLibrary' | 'nav.courses' | 'nav.attendance' | 'nav.conduct';
export type HomeNavShortLabelKey = 'nav.shortLectureLibrary' | 'nav.shortQuestionBank' | 'nav.shortDocumentLibrary' | 'nav.shortSampleLibrary' | 'nav.shortConduct';
export type HomeNavGroupLabelKey = 'nav.groupLectures' | 'nav.groupClassManagement';

export interface HomeNavItem {
  id: string;
  labelKey: HomeNavLabelKey;
  // Caption for the collapsed icon rail, which has ~72px to play with. Omitted where the full
  // label already fits. The full label stays the accessible name either way.
  shortLabelKey?: HomeNavShortLabelKey;
  icon: ComponentType<{ size?: number }>;
  // Route the item points at, or null while the screen does not exist - a null item renders
  // disabled rather than shipping a clickable dead end.
  route: string | null;
}

export interface HomeNavGroup {
  // null for the unlabeled top group
  labelKey: HomeNavGroupLabelKey | null;
  items: HomeNavItem[];
}

export const HOME_ROUTE = '/';
export const LECTURE_LIBRARY_ROUTE = '/kho-bai-giang';
export const QUESTION_BANK_ROUTE = '/kho-bai-tap';
export const CONDUCT_ROUTE = '/khen-thuong-ki-luat';
export const ATTENDANCE_ROUTE = '/diem-danh';
export const COURSES_ROUTE = '/khoa-hoc';
export const DOCUMENT_LIBRARY_ROUTE = '/kho-tai-lieu';

// Labels are the design's typed strings; home.css applies the uppercase Figma sets via textCase.
export const HOME_NAV: HomeNavGroup[] = [
  {
    labelKey: null,
    items: [
      { id: 'home', labelKey: 'nav.home', icon: HomeIcon, route: HOME_ROUTE },
    ],
  },
  {
    labelKey: 'nav.groupLectures',
    items: [
      { id: 'lecture-library', labelKey: 'nav.lectureLibrary', shortLabelKey: 'nav.shortLectureLibrary', icon: FolderIcon, route: LECTURE_LIBRARY_ROUTE },
      { id: 'question-bank', labelKey: 'nav.questionBank', shortLabelKey: 'nav.shortQuestionBank', icon: ClipboardIcon, route: QUESTION_BANK_ROUTE },
      { id: 'document-library', labelKey: 'nav.documentLibrary', shortLabelKey: 'nav.shortDocumentLibrary', icon: FileTextIcon, route: DOCUMENT_LIBRARY_ROUTE },
      { id: 'sample-library', labelKey: 'nav.sampleLibrary', shortLabelKey: 'nav.shortSampleLibrary', icon: ClipboardListIcon, route: null },
      { id: 'courses', labelKey: 'nav.courses', icon: BookOpenIcon, route: COURSES_ROUTE },
    ],
  },
  {
    labelKey: 'nav.groupClassManagement',
    items: [
      { id: 'attendance', labelKey: 'nav.attendance', icon: ClipboardCheckIcon, route: ATTENDANCE_ROUTE },
      { id: 'conduct', labelKey: 'nav.conduct', shortLabelKey: 'nav.shortConduct', icon: StarIcon, route: CONDUCT_ROUTE },
    ],
  },
];

export interface HomeNavReleasedItem extends HomeNavItem {
  route: string;
}

export interface HomeNavReleasedGroup {
  labelKey: HomeNavGroupLabelKey | null;
  items: HomeNavReleasedItem[];
}

// Kept for non-demo builds; the demo sidebar renders HOME_NAV whole so the menu shows the full surface.
export function releasedNavGroups(): HomeNavReleasedGroup[] {
  const groups: HomeNavReleasedGroup[] = [];
  HOME_NAV.forEach((group) => {
    const items: HomeNavReleasedItem[] = [];
    group.items.forEach((item) => { if (item.route !== null) items.push({ ...item, route: item.route }); });
    if (items.length > 0) groups.push({ labelKey: group.labelKey, items: items });
  });
  return groups;
}
